'use client'

import { type ReactNode, useRef } from 'react'
import Link from 'next/link'

import { Icon, IconComponent } from '@graftcode/design-system'

import { AssetSource } from '@/modules/academy/enums'
import { ContentNav } from '@/shared/components/ContentNav'
import { MarkdownRenderer } from '@/shared/components/MarkdownRenderer'
import { DOCUMENTATION_ARTICLE_TEST_IDS } from '@/shared/docs-test-ids.const'
import { stripLeadingMarkdownH1 } from '@/shared/utils/stripLeadingMarkdownH1'

import { useScrollToSearchMatch } from './hooks/useScrollToSearchMatch'

import styles from './DocumentationArticle.module.scss'

interface NavLink {
  title: string
  href: string
}

interface DocumentationArticleProps {
  content: string
  title: string
  currentPath: string
  assetSource: AssetSource
  prevLink?: NavLink | null
  nextLink?: NavLink | null
  headerExtra?: ReactNode
  navigation?: ReactNode
  hideContentNav?: boolean
  highlightQuery?: string
}

export function DocumentationArticle({
  content,
  title,
  currentPath,
  assetSource,
  prevLink,
  nextLink,
  headerExtra,
  navigation,
  hideContentNav,
  highlightQuery = '',
}: DocumentationArticleProps) {
  const body = stripLeadingMarkdownH1(content)
  const contentRef = useRef<HTMLDivElement>(null)
  useScrollToSearchMatch(contentRef, currentPath, highlightQuery)

  return (
    <div data-testid={DOCUMENTATION_ARTICLE_TEST_IDS.root} className={styles.container}>
      <article className={styles.article}>
        <h1 className={styles.title}>{title}</h1>
        {headerExtra}
        <div ref={contentRef}>
          <MarkdownRenderer content={body} currentPath={currentPath} assetSource={assetSource} />
        </div>

        {navigation ?? (
          <div className={styles.navigation}>
            {prevLink ? (
              <Link href={prevLink.href} className={styles.navCard}>
                <span className={styles.navLabel}>Previous</span>
                <span className={styles.navTitleRow}>
                  <IconComponent icon={Icon.ChevronLeftRegular} size={20} />
                  <span className={styles.navSectionTitle}>{prevLink.title}</span>
                </span>
              </Link>
            ) : (
              <div className={styles.navSpacer} />
            )}

            {nextLink ? (
              <Link href={nextLink.href} className={`${styles.navCard} ${styles.navCardNext}`}>
                <span className={styles.navLabel}>Next</span>
                <span className={styles.navTitleRow}>
                  <span className={styles.navSectionTitle}>{nextLink.title}</span>
                  <IconComponent icon={Icon.ChevronRightRegular} size={20} />
                </span>
              </Link>
            ) : (
              <div className={styles.navSpacer} />
            )}
          </div>
        )}
      </article>
      {!hideContentNav && <ContentNav content={body} />}
    </div>
  )
}
